'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { authService } from '../lib/auth-service';
import { SIGN_IN_REDIRECTING } from '../lib/twitter-auth-service';
import { meetsMinimumRole } from '../lib/auth-roles';
import { User } from '../types/content';
import {
  Permission,
  PermissionChecker,
  PermissionContext,
  ResourceContext,
  UserRole,
  getPermissionForAction,
  ArticleAction,
  MediaAction,
  UserAction
} from '../types/permissions';

interface AuthContextType {
  user: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  // Set when a mobile redirect sign-in was returning but FAILED to complete (null otherwise).
  // Sign-in walls (ContributeGuard) surface it so the user isn't silently stranded on return.
  redirectSignInError: string | null;
  signIn: (email: string, password: string) => Promise<User | null>;
  // Resolves to the signed-in user, null (no user), or SIGN_IN_REDIRECTING when the mobile
  // popup fallback triggered a full-page redirect (sign-in completes on the return load).
  signInWithTwitter: () => Promise<User | typeof SIGN_IN_REDIRECTING | null>;
  signOut: () => Promise<void>;

  // Legacy permission check (deprecated - use checkPermission instead)
  hasPermission: (role?: 'writer' | 'editor' | 'admin') => boolean;

  // New granular permission system
  checkPermission: (permission: Permission, resource?: ResourceContext) => boolean;
  canPerformAction: (action: ArticleAction | MediaAction | UserAction, scope?: 'own' | 'all') => boolean;
  hasRole: (role: UserRole) => boolean;
  hasMinimumRole: (minimumRole: UserRole) => boolean;
  canAccessAdmin: () => boolean;
  canAccessEditor: () => boolean;

  // User context utilities
  getUserPermissionContext: () => PermissionContext | null;

  refreshUserData: () => Promise<void>;

  // RBAC diagnostic tools
  runRBACDiagnostic: () => Promise<void>;
  fixLegacyArticles: (adminUserId?: string) => Promise<any>;

  // Author field migration
  runAuthorFieldMigration: () => Promise<any>;
  previewAuthorFieldMigration: () => Promise<void>;

  // RBAC verification
  runRBACVerification: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Best-effort session-cookie sync (WS-K, Part 2 / H1).
 *
 * Posts the user's fresh Firebase ID token to /api/auth/session so the SERVER can mint an
 * httpOnly __session cookie, which the /admin RSC guard reads on navigation. This is
 * purely additive defense-in-depth: any failure here must NOT break login, so every call
 * is wrapped and swallowed. The client app keeps working off the in-memory Firebase auth
 * state regardless of whether the cookie was set.
 */
async function setSessionCookie(firebaseUser: FirebaseUser): Promise<void> {
  try {
    const idToken = await firebaseUser.getIdToken();
    await fetch('/api/auth/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });
  } catch {
    // Best-effort: server guard degrades to the client ProtectedRoute if this fails.
  }
}

async function clearSessionCookie(): Promise<void> {
  try {
    await fetch('/api/auth/session', { method: 'DELETE' });
  } catch {
    // Best-effort.
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectSignInError, setRedirectSignInError] = useState<string | null>(null);

  useEffect(() => {
    const activeAuth = auth;
    if (!activeAuth) return;

    let unsubscribe: () => void = () => {};
    let cancelled = false;

    (async () => {
      // Finish any pending Twitter redirect sign-in (mobile popup fallback) BEFORE we start
      // reading auth state. For a brand-new contributor this creates the Firestore user doc,
      // which the onAuthStateChanged read below would otherwise miss. A no-op (no redirect
      // pending) returns silently; a redirect that WAS returning but failed throws — we surface
      // that to the user instead of stranding them, but STILL wire up onAuthStateChanged.
      try {
        await authService.completeTwitterRedirectSignIn();
      } catch (err) {
        console.error('Twitter redirect sign-in error:', err);
        if (!cancelled) setRedirectSignInError("Sign-in didn't complete. Please try again.");
      }
      if (cancelled) return;

      unsubscribe = onAuthStateChanged(activeAuth, async (firebaseUser) => {
        setUser(firebaseUser);

        if (firebaseUser) {
          // Get user data from Firestore
          const userData = await authService.getUserData(firebaseUser.uid);
          setUserData(userData);
          // Best-effort: mint the server-side session cookie for the /admin RSC guard.
          // Fire-and-forget — never blocks login or gates UI on cookie success.
          void setSessionCookie(firebaseUser);
        } else {
          setUserData(null);
          // Best-effort: clear the server-side session cookie on sign-out.
          void clearSessionCookie();
        }

        setLoading(false);
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<User | null> => {
    const userData = await authService.signIn(email, password);
    setUserData(userData);
    return userData;
  };

  const signInWithTwitter = async (): Promise<User | typeof SIGN_IN_REDIRECTING | null> => {
    // A fresh attempt supersedes any stale "redirect didn't complete" failure — clear it so
    // a retry doesn't show the old message alongside the new attempt.
    setRedirectSignInError(null);
    const result = await authService.signInWithTwitter();
    // Redirect fallback in flight: the page is navigating to X and no user resolves here.
    // Don't clobber state — sign-in completes on the return load via the useEffect above.
    if (result === SIGN_IN_REDIRECTING) {
      return SIGN_IN_REDIRECTING;
    }
    setUserData(result);
    return result;
  };

  const signOut = async (): Promise<void> => {
    await authService.signOut();
    setUserData(null);
  };

  // Legacy permission check (deprecated)
  const hasPermission = (role: 'writer' | 'editor' | 'admin' = 'writer'): boolean => {
    if (!userData || !userData.isActive) return false;

    if (role === 'admin') {
      return userData.role === 'admin';
    }

    if (role === 'editor') {
      return userData.role === 'editor' || userData.role === 'admin';
    }

    return userData.role === 'writer' || userData.role === 'editor' || userData.role === 'admin';
  };

  // New granular permission system with per-user grants support
  const checkPermission = (permission: Permission, resource?: ResourceContext): boolean => {
    if (!userData || !userData.isActive) return false;

    const permissionContext: PermissionContext = {
      user: {
        id: userData.id!,
        role: userData.role,
        isActive: userData.isActive,
      },
      resource,
    };

    // First check role-based permissions
    if (PermissionChecker.canPerformAction(permissionContext, permission)) {
      return true;
    }

    // Then check per-user granted permissions
    // This allows granting specific capabilities beyond the user's role
    // e.g., a writer with 'breaking_news_create' grant
    if (userData.grantedPermissions?.includes(permission)) {
      // For ownership-based permissions, still need to verify ownership
      // e.g., BREAKING_NEWS_EDIT_OWN requires the user to be the author
      if (resource) {
        return PermissionChecker.checkResourceOwnership(
          { id: userData.id!, role: userData.role },
          permission,
          resource
        );
      }
      return true;
    }

    return false;
  };

  const canPerformAction = (
    action: ArticleAction | MediaAction | UserAction,
    scope: 'own' | 'all' = 'own'
  ): boolean => {
    if (!userData || !userData.isActive) return false;

    try {
      const permission = getPermissionForAction(action, scope);

      // Build resource context based on action
      let resource: ResourceContext | undefined;
      if ('articleId' in action && action.authorId) {
        resource = { authorId: action.authorId };
      } else if ('mediaId' in action && action.uploadedBy) {
        resource = { uploadedBy: action.uploadedBy };
      } else if ('targetUserId' in action && action.targetUserId) {
        resource = { ownerId: action.targetUserId };
      }

      return checkPermission(permission, resource);
    } catch (error) {
      console.error('Error checking action permission:', error);
      return false;
    }
  };

  const hasRole = (role: UserRole): boolean => {
    return userData?.role === role && userData.isActive;
  };

  const hasMinimumRole = (minimumRole: UserRole): boolean => {
    if (!userData || !userData.isActive) return false;
    // Hierarchy lives in the shared pure helper (single source of truth):
    // contributor(1) < writer(2) < editor(3) < admin(4) < owner(5). Unknown roles → 0.
    return meetsMinimumRole(userData.role, minimumRole);
  };

  const canAccessAdmin = (): boolean => {
    return checkPermission(Permission.ADMIN_MENU_ACCESS);
  };

  const canAccessEditor = (): boolean => {
    return checkPermission(Permission.EDITOR_MENU_ACCESS) ||
      checkPermission(Permission.ADMIN_MENU_ACCESS);
  };

  const getUserPermissionContext = (): PermissionContext | null => {
    if (!userData || !userData.isActive) return null;

    return {
      user: {
        id: userData.id!,
        role: userData.role,
        isActive: userData.isActive,
      },
    };
  };

  const refreshUserData = async (): Promise<void> => {
    if (user?.uid) {
      const freshUserData = await authService.getUserData(user.uid);
      setUserData(freshUserData);
    }
  };

  // RBAC diagnostic functions
  const runRBACDiagnostic = async () => {
    try {
      // RBAC migration utilities have been removed - functionality completed

    } catch (error) {
      console.error('Error running RBAC diagnostic:', error);
    }
  };

  const fixLegacyArticles = async (adminUserId?: string) => {
    try {
      // Legacy article fix utilities have been removed - migration completed

      return { total: 0, fixed: 0, errors: 0 };
    } catch (error) {
      console.error('Error fixing legacy articles:', error);
    }
  };

  // Author field migration functions
  const runAuthorFieldMigration = async () => {
    try {
      // Author field migration utilities have been removed - migration completed

      return { total: 0, fixed: 0, errors: 0 };
    } catch (error) {
      console.error('Error running author field migration:', error);
      return { total: 0, fixed: 0, errors: 1 };
    }
  };

  const previewAuthorFieldMigration = async () => {
    try {
      // Author field migration utilities have been removed - migration completed

    } catch (error) {
      console.error('Error previewing author field migration:', error);
    }
  };

  // RBAC verification function
  const runRBACVerification = async () => {
    try {
      // RBAC verification utilities have been removed - verification completed

    } catch (error) {
      console.error('Error running RBAC verification:', error);
    }
  };


  const value: AuthContextType = {
    user,
    userData,
    loading,
    redirectSignInError,
    signIn,
    signInWithTwitter,
    signOut,
    hasPermission,
    checkPermission,
    canPerformAction,
    hasRole,
    hasMinimumRole,
    canAccessAdmin,
    canAccessEditor,
    getUserPermissionContext,
    refreshUserData,
    // RBAC diagnostic tools
    runRBACDiagnostic,
    fixLegacyArticles,
    // Author field migration
    runAuthorFieldMigration,
    previewAuthorFieldMigration,
    // RBAC verification
    runRBACVerification,
  };

  // Expose auth context to window for console debugging (dev only)
  React.useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      (window as any).__AUTH_CONTEXT__ = value;
    }
  }, [value]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
